import { forwardRef, useId, useRef, useState } from 'react'
import { cx, IconButton } from './primitives.jsx'

/**
 * Form primitives.
 *
 * Every control here is *uncontrolled about appearance and controlled about
 * data*: it renders a semantic class the theme dresses, and it always takes
 * `value` + `onChange` so the admin form stays a pure function of its record.
 *
 * Accessibility is handled once, here, rather than at each of the ~40 call
 * sites in the admin console: label association, error announcement, and hint
 * wiring all go through `Field`.
 *
 * 表单基础组件。
 *
 * 这里的每个控件都「外观不可控、数据受控」：只输出由主题负责外观的语义类名，
 * 并且始终接受 value + onChange，让后台表单保持为「记录的纯函数」。
 *
 * 无障碍在这里统一处理一次，而不是散落到后台约 40 个调用点：标签关联、错误播报、
 * 提示文本关联全部走 Field。
 */

/**
 * Labelled form row wrapping any control.
 *
 * 包裹任意控件的带标签表单行。
 *
 * @param {object} props Props / 属性。
 * @param {React.ReactNode} props.label Field label / 字段标签。
 * @param {string} [props.hint] Helper text below the control / 控件下方的说明。
 * @param {string} [props.error] Error message; presence sets the invalid state / 错误信息，存在即进入错误态。
 * @param {boolean} [props.required] Mark as required / 标记必填。
 * @param {(id: string, describedBy: string|undefined) => React.ReactNode} props.children Render prop / 渲染函数。
 * @returns {JSX.Element} Field row / 表单行。
 */
export function Field({ label, hint, error, required = false, className, children }) {
  const id = useId()
  const hintId = hint ? `${id}-hint` : undefined
  const errorId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined

  return (
    <div className={cx('ui-field', error && 'is-invalid', className)}>
      {label && (
        <label className="ui-field__label" htmlFor={id}>
          {label}
          {required && (
            <span className="ui-field__required" aria-hidden="true">
              *
            </span>
          )}
        </label>
      )}
      <div className="ui-field__control">{children(id, describedBy)}</div>
      {hint && !error && (
        <p className="ui-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {/* role="alert" so a validation failure is announced, not just coloured.
          role="alert" 让校验失败被播报，而不只是变个颜色。 */}
      {error && (
        <p className="ui-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

/**
 * Single-line text input.
 *
 * 单行文本输入框。
 *
 * @param {object} props Standard input props / 标准 input 属性。
 * @returns {JSX.Element} Input / 输入框。
 */
export const TextInput = forwardRef(function TextInput({ className, ...rest }, ref) {
  return <input ref={ref} type="text" className={cx('ui-input', className)} {...rest} />
})

/**
 * Multi-line text input that grows with its content.
 *
 * 随内容自动增高的多行输入框。
 *
 * @param {object} props Props / 属性。
 * @param {number} [props.minRows] Minimum visible rows / 最少可见行数。
 * @returns {JSX.Element} Textarea / 多行输入框。
 */
export const TextArea = forwardRef(function TextArea({ className, minRows = 3, onChange, ...rest }, ref) {
  const innerRef = useRef(null)

  /**
   * Resize to fit content on every change.
   *
   * Height is reset to 'auto' first because scrollHeight never shrinks below
   * the current height — without the reset the box only ever grows.
   *
   * 每次变更后按内容调整高度。先把高度重置为 auto，因为 scrollHeight 不会低于
   * 当前高度——不重置的话输入框只会越变越高。
   */
  const autoSize = (element) => {
    if (!element) return
    element.style.height = 'auto'
    element.style.height = `${element.scrollHeight}px`
  }

  return (
    <textarea
      ref={(node) => {
        innerRef.current = node
        if (typeof ref === 'function') ref(node)
        else if (ref) ref.current = node
        autoSize(node)
      }}
      className={cx('ui-textarea', className)}
      rows={minRows}
      onChange={(event) => {
        autoSize(event.target)
        onChange?.(event)
      }}
      {...rest}
    />
  )
})

/**
 * Select dropdown.
 *
 * 下拉选择框。
 *
 * @param {object} props Props / 属性。
 * @param {Array<{value: string|number, label: string}>} props.options Options / 选项。
 * @returns {JSX.Element} Select / 选择框。
 */
export const Select = forwardRef(function Select({ options = [], className, children, ...rest }, ref) {
  return (
    <div className={cx('ui-select', className)}>
      <select ref={ref} className="ui-select__control" {...rest}>
        {children ||
          options.map((option) => (
            <option key={String(option.value)} value={option.value}>
              {option.label}
            </option>
          ))}
      </select>
      <span className="ui-select__chevron" aria-hidden="true">
        ▾
      </span>
    </div>
  )
})

/**
 * Boolean switch.
 *
 * Implemented as a real checkbox so keyboard and assistive tech get the right
 * semantics; the theme hides the box and draws the track.
 *
 * 布尔开关。用真正的 checkbox 实现，保证键盘和辅助技术拿到正确语义；
 * 主题隐藏原生方框并绘制轨道。
 *
 * @param {object} props Props / 属性。
 * @param {boolean} props.checked Current state / 当前状态。
 * @param {(next: boolean) => void} props.onChange Change handler / 变更回调。
 * @param {React.ReactNode} [props.label] Inline label / 行内标签。
 * @returns {JSX.Element} Switch / 开关。
 */
export function Switch({ checked, onChange, label, disabled = false, className, id }) {
  return (
    <label className={cx('ui-switch', disabled && 'is-disabled', className)}>
      <input
        id={id}
        type="checkbox"
        className="ui-switch__input"
        checked={!!checked}
        disabled={disabled}
        onChange={(event) => onChange?.(event.target.checked)}
      />
      <span className="ui-switch__track" aria-hidden="true">
        <span className="ui-switch__thumb" />
      </span>
      {label && <span className="ui-switch__label">{label}</span>}
    </label>
  )
}

/**
 * Tag input — a list of short strings entered one at a time.
 *
 * Enter and comma both commit; Backspace on an empty box removes the last tag,
 * which is the behaviour people expect from every tag field they have used.
 *
 * 标签输入 —— 逐个录入的短字符串列表。回车和逗号都提交；输入框为空时按退格删除
 * 最后一个标签——这是所有人从别处习得的预期行为。
 *
 * @param {object} props Props / 属性。
 * @param {string[]} props.value Current tags / 当前标签。
 * @param {(next: string[]) => void} props.onChange Change handler / 变更回调。
 * @param {string} [props.placeholder] Placeholder / 占位文字。
 * @returns {JSX.Element} Tag input / 标签输入。
 */
export function TagInput({ value = [], onChange, placeholder, id, 'aria-describedby': describedBy, className }) {
  const [draft, setDraft] = useState('')
  const tags = Array.isArray(value) ? value : []

  const commit = (raw) => {
    const next = String(raw || '').trim()
    if (!next || tags.includes(next)) {
      setDraft('')
      return
    }
    onChange?.([...tags, next])
    setDraft('')
  }

  return (
    <div className={cx('ui-taginput', className)}>
      {tags.map((tag, index) => (
        <span className="ui-taginput__tag" key={`${tag}-${index}`}>
          {tag}
          <button
            type="button"
            className="ui-taginput__remove"
            aria-label={`Remove ${tag}`}
            onClick={() => onChange?.(tags.filter((_, i) => i !== index))}
          >
            ×
          </button>
        </span>
      ))}
      <input
        id={id}
        aria-describedby={describedBy}
        className="ui-taginput__input"
        value={draft}
        placeholder={tags.length ? '' : placeholder}
        onChange={(event) => {
          // A typed comma commits rather than becoming part of the tag.
          // 输入逗号即提交，而不是把逗号当作标签内容。
          if (event.target.value.includes(',')) commit(event.target.value.replace(/,/g, ''))
          else setDraft(event.target.value)
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit(draft)
          } else if (event.key === 'Backspace' && !draft && tags.length) {
            onChange?.(tags.slice(0, -1))
          }
        }}
        onBlur={() => commit(draft)}
      />
    </div>
  )
}

/**
 * File drop zone.
 *
 * Purely a *picker*: it surfaces a File to its caller and knows nothing about
 * uploading, progress or storage. That keeps upload policy in one place
 * (src/lib) instead of smeared across the UI layer.
 *
 * 文件拖放区。它只是一个「选择器」：把 File 交给调用方，对上传、进度、存储一无所知。
 * 这样上传策略集中在 src/lib 一处，而不是摊在 UI 层各处。
 *
 * @param {object} props Props / 属性。
 * @param {(files: File[]) => void} props.onFiles Selected files / 已选文件。
 * @param {string} [props.accept] Input accept attribute / accept 属性。
 * @param {boolean} [props.multiple] Allow several files / 允许多选。
 * @param {React.ReactNode} [props.children] Custom body / 自定义内容。
 * @returns {JSX.Element} Drop zone / 拖放区。
 */
export function FileDrop({ onFiles, accept, multiple = false, disabled = false, className, children, id }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  const handleFiles = (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean)
    if (files.length) onFiles?.(multiple ? files : [files[0]])
  }

  return (
    <div
      className={cx('ui-filedrop', dragging && 'is-dragging', disabled && 'is-disabled', className)}
      onDragOver={(event) => {
        if (disabled) return
        event.preventDefault()
        setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        if (disabled) return
        event.preventDefault()
        setDragging(false)
        handleFiles(event.dataTransfer.files)
      }}
    >
      <input
        id={id}
        ref={inputRef}
        type="file"
        className="ui-filedrop__input"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files)
          // Reset so picking the same file twice in a row still fires onChange.
          // 重置输入，保证连续两次选同一个文件也能触发 onChange。
          event.target.value = ''
        }}
      />
      <button
        type="button"
        className="ui-filedrop__trigger"
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
      >
        {children}
      </button>
    </div>
  )
}

/**
 * Compact preview of an already-uploaded asset, with a remove control.
 *
 * 已上传资源的紧凑预览，带移除按钮。
 *
 * @param {object} props Props / 属性。
 * @param {string} props.url Asset URL / 资源地址。
 * @param {string} [props.name] Display name / 显示名称。
 * @param {'image'|'video'|'audio'|'doc'} [props.kind] Asset kind / 资源类型。
 * @param {() => void} [props.onRemove] Remove handler / 移除回调。
 * @param {string} props.removeLabel Accessible label for remove / 移除按钮的可访问名称。
 * @returns {JSX.Element} Asset preview / 资源预览。
 */
export function AssetPreview({ url, name, kind = 'image', onRemove, removeLabel, className }) {
  return (
    <div className={cx('ui-asset', `ui-asset--${kind}`, className)}>
      {kind === 'image' ? (
        <img className="ui-asset__thumb" src={url} alt={name || ''} loading="lazy" />
      ) : (
        <span className="ui-asset__glyph" aria-hidden="true">
          {kind === 'video' ? '▶' : kind === 'audio' ? '♪' : '📄'}
        </span>
      )}
      <a className="ui-asset__name ui-link" href={url} target="_blank" rel="noopener noreferrer">
        {name || url.split('/').pop()}
      </a>
      {onRemove && (
        <IconButton size="sm" label={removeLabel} className="ui-asset__remove" onClick={onRemove}>
          ×
        </IconButton>
      )}
    </div>
  )
}
